import React, { useMemo, useRef, useState, useCallback } from 'react';
import { Box, Stack, Paper, Divider, Typography, Slider, Alert } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import {
    useFieldArray,
    type Control,
    type FieldErrors,
    type UseFormSetValue,
} from 'react-hook-form';

import {
    SimpleButton,
    SimpleSwitch,
    ControlledTextField,
    ControlledSwitch,
    ErrorMessage,
} from '@/src/ui';

import {
    type HotspotRegion,
    type QtiHotspotQuestionDTO,
    type RectDropZoneGeometry,
    type CircleDropZoneGeometry,
    type NormalizedPoint,
} from '@/src/lib';
import {
    getImageSize,
    putImageBlob,
    addImageRef,
    releaseImageRef,
    getResolvedSrc,
} from '@/src/utils';
import { resizeToMax, extFromFileNameOrType } from '../utils/imageHelpers';

type Tool = 'select' | 'rect' | 'circle';

const HANDLE_SIZE = 8;

interface HSRegionEditorProps {
    control: Control<QtiHotspotQuestionDTO>;
    errors: FieldErrors<QtiHotspotQuestionDTO>;
    question: QtiHotspotQuestionDTO;
    currentLanguage: 'en' | 'es';
    setValue: UseFormSetValue<QtiHotspotQuestionDTO>;
}

interface DragState {
    kind: 'move' | 'resize' | null;
    regionIndex?: number;
    handle?: 'se' | 'r';
    startPx?: { x: number; y: number };
    startRegion?: HotspotRegion;
}

// ── Overlap detection ──────────────────────────────────────────────────
interface OverlapWarning {
    regionA: string;
    regionB: string;
}

function detectOverlaps(regions: HotspotRegion[], lang: 'en' | 'es'): OverlapWarning[] {
    const warnings: OverlapWarning[] = [];
    for (let i = 0; i < regions.length; i++) {
        for (let j = i + 1; j < regions.length; j++) {
            const gA =
                lang === 'en'
                    ? regions[i].geometry
                    : (regions[i].geometry_es ?? regions[i].geometry);
            const gB =
                lang === 'en'
                    ? regions[j].geometry
                    : (regions[j].geometry_es ?? regions[j].geometry);
            if (geometriesOverlap(gA, gB)) {
                warnings.push({ regionA: regions[i].label, regionB: regions[j].label });
            }
        }
    }
    return warnings;
}

function geometriesOverlap(
    a: RectDropZoneGeometry | CircleDropZoneGeometry,
    b: RectDropZoneGeometry | CircleDropZoneGeometry
): boolean {
    const boundsA = toBounds(a);
    const boundsB = toBounds(b);
    return !(
        boundsA.right <= boundsB.left ||
        boundsB.right <= boundsA.left ||
        boundsA.bottom <= boundsB.top ||
        boundsB.bottom <= boundsA.top
    );
}

function toBounds(g: RectDropZoneGeometry | CircleDropZoneGeometry) {
    if (g.type === 'rect') {
        return {
            left: g.x,
            top: g.y,
            right: g.x + g.width,
            bottom: g.y + g.height,
        };
    }
    return {
        left: g.cx - g.r,
        top: g.cy - g.r,
        right: g.cx + g.r,
        bottom: g.cy + g.r,
    };
}

// ── Main component ─────────────────────────────────────────────────────
export default function HSRegionEditor(props: HSRegionEditorProps) {
    const { control, errors, currentLanguage, question, setValue } = props;

    const { backgroundImageUrl, backgroundImageUrl_es, designCanvas, designCanvas_es, regions } =
        question;
    const activeCanvas = currentLanguage === 'en' ? designCanvas : designCanvas_es;

    const { append, remove, update, insert } = useFieldArray({
        control,
        name: 'regions',
    });

    const [tool, setTool] = useState<Tool>('select');
    const [zoom, setZoom] = useState<number>(1);
    const [showGrid, setShowGrid] = useState<boolean>(false);
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

    const containerRef = useRef<HTMLDivElement>(null);
    const svgRef = useRef<SVGSVGElement>(null);
    const [drag, setDrag] = useState<DragState>({ kind: null });

    // File input refs (matches DDZoneEditor pattern)
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const fileInputRef_es = useRef<HTMLInputElement | null>(null);

    // Overlap warnings
    const overlaps = useMemo(
        () => detectOverlaps(regions ?? [], currentLanguage),
        [regions, currentLanguage]
    );

    // Translate between px and normalized
    const toNorm = useCallback(
        (px: { x: number; y: number }): NormalizedPoint => ({
            x: snap(px.x / (activeCanvas.width * zoom)),
            y: snap(px.y / (activeCanvas.height * zoom)),
        }),
        [activeCanvas, zoom]
    );

    const toPx = useCallback(
        (np: NormalizedPoint): { x: number; y: number } => ({
            x: np.x * activeCanvas.width * zoom,
            y: np.y * activeCanvas.height * zoom,
        }),
        [activeCanvas, zoom]
    );

    const getMouse = (e: React.MouseEvent) => {
        const svg = svgRef.current!;
        const rect = svg.getBoundingClientRect();
        return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const nextZIndex = () =>
        (regions ?? []).length
            ? (regions[regions.length - 1]?.zIndex ?? regions.length - 1) + 1
            : 0;

    // ── Image upload (matches DDZoneEditor exactly) ────────────────────
    const onPickFile = async (file: File) => {
        if (!file) return;

        // Release old image ref if replacing
        if (
            currentLanguage === 'en' &&
            backgroundImageUrl &&
            backgroundImageUrl.startsWith('local://')
        ) {
            const guid = backgroundImageUrl.slice('local://'.length);
            releaseImageRef(guid);
        } else if (
            currentLanguage === 'es' &&
            backgroundImageUrl_es &&
            backgroundImageUrl_es.startsWith('local://')
        ) {
            const guid = backgroundImageUrl_es.slice('local://'.length);
            releaseImageRef(guid);
        }

        const resized = await resizeToMax(file, file.type || 'image/png');

        const fileName = `${uuidv4()}${extFromFileNameOrType(file.name, file.type)}`;

        putImageBlob(fileName, resized);

        const localUrl = `local://${fileName}`;
        const { width: w, height: h } = await getImageSize(resized);

        if (currentLanguage === 'en') {
            setValue('backgroundImageUrl', localUrl);
            setValue('designCanvas', { width: w, height: h });
        } else {
            setValue('backgroundImageUrl_es', localUrl);
            setValue('designCanvas_es', { width: w, height: h });
        }

        addImageRef(fileName);
    };

    // ── Drawing new regions ────────────────────────────────────────────
    const startRect = (start: { x: number; y: number }) => {
        const id = `rect-${uuidv4()}`;
        const norm = toNorm(start);
        const region: HotspotRegion = {
            id,
            label: `Region ${(regions?.length ?? 0) + 1}`,
            zIndex: nextZIndex(),
            visible: false,
            weight: 1,
            geometry: {
                type: 'rect',
                x: norm.x,
                y: norm.y,
                width: 0.05,
                height: 0.05,
                cornerRadius: 0,
            },
            geometry_es: {
                type: 'rect',
                x: norm.x,
                y: norm.y,
                width: 0.05,
                height: 0.05,
                cornerRadius: 0,
            },
        };
        append(region as any);
        setSelectedIndex(regions?.length ?? 0);
        setDrag({
            kind: 'resize',
            regionIndex: regions?.length ?? 0,
            handle: 'se',
            startPx: start,
            startRegion: region,
        });
    };

    const startCircle = (start: { x: number; y: number }) => {
        const id = `circle-${uuidv4()}`;
        const c = toNorm(start);
        const region: HotspotRegion = {
            id,
            label: `Region ${(regions?.length ?? 0) + 1}`,
            zIndex: nextZIndex(),
            visible: false,
            weight: 1,
            geometry: {
                type: 'circle',
                cx: c.x,
                cy: c.y,
                r: 0.05,
            },
            geometry_es: {
                type: 'circle',
                cx: c.x,
                cy: c.y,
                r: 0.05,
            },
        };
        append(region as any);
        setSelectedIndex(regions?.length ?? 0);
        setDrag({
            kind: 'resize',
            regionIndex: regions?.length ?? 0,
            handle: 'r',
            startPx: start,
            startRegion: region,
        });
    };

    const onSvgMouseDown = (e: React.MouseEvent) => {
        const pos = getMouse(e);
        if (tool === 'rect') return startRect(pos);
        if (tool === 'circle') return startCircle(pos);
        if (tool === 'select') setSelectedIndex(null);
    };

    // ── Move / Resize ──────────────────────────────────────────────────
    const onSvgMouseMove = (e: React.MouseEvent) => {
        const pos = getMouse(e);
        if (!drag.kind || drag.regionIndex == null) return;

        const dz = regions[drag.regionIndex];
        if (!dz || !drag.startRegion || !drag.startPx) return;

        const getGeometry = (r: any) => (currentLanguage === 'en' ? r.geometry : r.geometry_es);
        const setGeometry = (r: any, geometry: any) =>
            currentLanguage === 'en' ? { ...r, geometry } : { ...r, geometry_es: geometry };

        if (drag.kind === 'resize') {
            const startGeom = getGeometry(drag.startRegion);

            if (startGeom.type === 'rect' && drag.handle === 'se') {
                const current = toNorm(pos);
                const x0 = startGeom.x;
                const y0 = startGeom.y;
                let w = current.x - x0;
                let h = current.y - y0;
                let nx = x0;
                let ny = y0;
                if (w < 0) {
                    nx = x0 + w;
                    w = Math.abs(w);
                }
                if (h < 0) {
                    ny = y0 + h;
                    h = Math.abs(h);
                }
                nx = snap(nx);
                ny = snap(ny);
                const nextW = Math.max(snap(w), 0.002);
                const nextH = Math.max(snap(h), 0.002);
                const newGeom = {
                    ...startGeom,
                    type: 'rect' as const,
                    x: nx,
                    y: ny,
                    width: nextW,
                    height: nextH,
                    cornerRadius: startGeom.cornerRadius,
                };
                update(drag.regionIndex, setGeometry(dz, newGeom) as any);
            } else if (startGeom.type === 'circle' && drag.handle === 'r') {
                const centerPx = toPx({ x: startGeom.cx, y: startGeom.cy });
                const dx = pos.x - centerPx.x;
                const dy = pos.y - centerPx.y;
                const rp = Math.sqrt(dx * dx + dy * dy);
                const rn = rp / (activeCanvas.width * zoom);
                const nextR = Math.max(snap(rn), 0.002);
                const newGeom = { ...startGeom, type: 'circle' as const, r: nextR };
                update(drag.regionIndex, setGeometry(dz, newGeom) as any);
            }
        } else if (drag.kind === 'move') {
            const startGeom = getGeometry(drag.startRegion);
            const dzGeom = getGeometry(dz);
            const deltaPx = { x: pos.x - drag.startPx.x, y: pos.y - drag.startPx.y };
            const deltaNorm = {
                x: deltaPx.x / (activeCanvas.width * zoom),
                y: deltaPx.y / (activeCanvas.height * zoom),
            };
            if (dzGeom.type === 'rect') {
                const s = startGeom as RectDropZoneGeometry;
                const newGeom = {
                    ...dzGeom,
                    x: snap(s.x + deltaNorm.x),
                    y: snap(s.y + deltaNorm.y),
                };
                update(drag.regionIndex, setGeometry(dz, newGeom) as any);
            } else {
                const s = startGeom as CircleDropZoneGeometry;
                const newGeom = {
                    ...dzGeom,
                    cx: snap(s.cx + deltaNorm.x),
                    cy: snap(s.cy + deltaNorm.y),
                };
                update(drag.regionIndex, setGeometry(dz, newGeom) as any);
            }
        }
    };

    const onSvgMouseUp = () => setDrag({ kind: null });

    // ── Region interaction (select / begin move) ───────────────────────
    const beginMove = (index: number) => (e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedIndex(index);
        setDrag({
            kind: 'move',
            regionIndex: index,
            startPx: getMouse(e),
            startRegion: deepCopy(regions[index]),
        });
    };

    const beginResizeRect = (index: number, _handle: 'se') => (e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedIndex(index);
        setDrag({
            kind: 'resize',
            regionIndex: index,
            handle: 'se',
            startPx: getMouse(e),
            startRegion: deepCopy(regions[index]),
        });
    };

    const beginResizeCircle = (index: number) => (e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedIndex(index);
        setDrag({
            kind: 'resize',
            regionIndex: index,
            handle: 'r',
            startPx: getMouse(e),
            startRegion: deepCopy(regions[index]),
        });
    };

    // ── Grid lines ─────────────────────────────────────────────────────
    const gridLines = useMemo(() => {
        if (!showGrid) return null;
        const step = 0.1;
        const lines: React.JSX.Element[] = [];
        for (let t = step; t < 1; t += step) {
            const vx = toPx({ x: t, y: 0 }).x;
            const vy = toPx({ x: 0, y: t }).y;
            lines.push(
                <line
                    key={`v-${t}`}
                    x1={vx}
                    y1={0}
                    x2={vx}
                    y2={activeCanvas.height * zoom}
                    stroke="#eee"
                    strokeWidth={1}
                />,
                <line
                    key={`h-${t}`}
                    x1={0}
                    y1={vy}
                    x2={activeCanvas.width * zoom}
                    y2={vy}
                    stroke="#eee"
                    strokeWidth={1}
                />
            );
        }
        return lines;
    }, [showGrid, activeCanvas, zoom, toPx]);

    // ── Selected region helpers ────────────────────────────────────────
    const selectedRegion = selectedIndex != null ? regions[selectedIndex] : null;

    const removeSelected = () => {
        if (selectedIndex == null) return;
        remove(selectedIndex);
        setSelectedIndex(null);
    };

    const duplicateSelected = () => {
        if (selectedIndex == null || !selectedRegion) return;
        const copy = deepCopy(selectedRegion);
        copy.id = `${copy.geometry.type}-${uuidv4()}`;
        copy.label = `${copy.label} (copy)`;
        copy.zIndex = nextZIndex();
        insert(selectedIndex + 1, copy as any);
        setSelectedIndex(selectedIndex + 1);
    };

    // ── Render ─────────────────────────────────────────────────────────
    return (
        <Stack direction="column" spacing={2}>
            {/* Overlap warnings */}
            {overlaps.length > 0 && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                    <Typography variant="body2" fontWeight="bold">
                        Overlapping regions detected:
                    </Typography>
                    {overlaps.map((o, i) => (
                        <Typography key={i} variant="caption" display="block">
                            &bull; &ldquo;{o.regionA}&rdquo; overlaps with &ldquo;{o.regionB}&rdquo;
                        </Typography>
                    ))}
                </Alert>
            )}

            {/* Toolbar */}
            <Paper elevation={1} sx={{ p: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <SimpleButton
                        onClick={() => setTool('select')}
                        variant={tool === 'select' ? 'contained' : 'outlined'}
                    >
                        Select
                    </SimpleButton>
                    <SimpleButton
                        onClick={() => setTool('rect')}
                        variant={tool === 'rect' ? 'contained' : 'outlined'}
                    >
                        Rect
                    </SimpleButton>
                    <SimpleButton
                        onClick={() => setTool('circle')}
                        variant={tool === 'circle' ? 'contained' : 'outlined'}
                    >
                        Circle
                    </SimpleButton>

                    <Divider flexItem orientation="vertical" sx={{ mx: 1 }} />

                    <Box sx={{ width: 180, px: 2 }}>
                        <Typography variant="caption">Zoom</Typography>
                        <Slider
                            size="small"
                            min={0.5}
                            max={2}
                            step={0.1}
                            value={zoom}
                            onChange={(_, v) => setZoom(v as number)}
                        />
                    </Box>
                    <SimpleSwitch
                        name="showGrid"
                        label="Show Grid"
                        checked={showGrid}
                        onChange={(e) => setShowGrid(e.target.checked)}
                    />

                    {/* Image upload — ref-based hidden input (matches DDZoneEditor) */}
                    {currentLanguage === 'en' ? (
                        <>
                            <SimpleButton onClick={() => fileInputRef.current?.click()}>
                                Upload Background Image
                            </SimpleButton>
                            <input
                                type="file"
                                accept="image/*"
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) onPickFile(file);
                                }}
                            />
                            <ErrorMessage error={errors.backgroundImageUrl} />
                        </>
                    ) : (
                        <>
                            <SimpleButton onClick={() => fileInputRef_es.current?.click()}>
                                Upload Background Image (Spanish)
                            </SimpleButton>
                            <input
                                type="file"
                                accept="image/*"
                                ref={fileInputRef_es}
                                style={{ display: 'none' }}
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) onPickFile(file);
                                }}
                            />
                            <ErrorMessage error={errors.backgroundImageUrl_es} />
                        </>
                    )}
                </Stack>

                <div className="my-4" />

                {/* Canvas with background image */}
                {(backgroundImageUrl && currentLanguage === 'en') ||
                (backgroundImageUrl_es && currentLanguage === 'es') ? (
                    <Box
                        ref={containerRef}
                        sx={{
                            mt: 1,
                            position: 'relative',
                            width: activeCanvas.width * zoom,
                            height: activeCanvas.height * zoom,
                            backgroundImage:
                                currentLanguage === 'en'
                                    ? `url(${getResolvedSrc(backgroundImageUrl)})`
                                    : `url(${getResolvedSrc(backgroundImageUrl_es)})`,
                            backgroundRepeat: 'no-repeat',
                            backgroundSize: '100% 100%',
                            border: '1px solid #ddd',
                            borderRadius: 1,
                            overflow: 'hidden',
                            cursor: tool === 'select' ? 'default' : 'crosshair',
                        }}
                    >
                        <svg
                            ref={svgRef}
                            width={activeCanvas.width * zoom}
                            height={activeCanvas.height * zoom}
                            onMouseDown={onSvgMouseDown}
                            onMouseMove={onSvgMouseMove}
                            onMouseUp={onSvgMouseUp}
                            onMouseLeave={onSvgMouseUp}
                            style={{ display: 'block' }}
                        >
                            {gridLines}

                            {(regions ?? [])
                                .map((r, i) => ({ r, i }))
                                .sort((a, b) => (a.r.zIndex ?? 0) - (b.r.zIndex ?? 0))
                                .map(({ r: region, i }) => {
                                    const isSelected = i === selectedIndex;
                                    const isError = !!errors.regions?.[i];
                                    const g =
                                        currentLanguage === 'en'
                                            ? region.geometry
                                            : (region.geometry_es ?? region.geometry);

                                    // Color: visible = blue, invisible = orange, error = red
                                    let fill = region.visible ? '#1976d25f' : '#ff98005f';
                                    if (isError) fill = '#F399995f';

                                    let stroke = region.visible ? '#00000066' : '#e65100';
                                    if (isError) stroke = '#F39999';
                                    else if (isSelected) stroke = '#1976d2';

                                    // Dashed stroke for invisible regions
                                    const strokeDash = !region.visible ? '6 4' : undefined;

                                    if (g.type === 'rect') {
                                        const p = toPx({ x: g.x, y: g.y });
                                        const w = g.width * activeCanvas.width * zoom;
                                        const h = g.height * activeCanvas.height * zoom;
                                        if (w < 0 || h < 0) return null;
                                        return (
                                            <g key={region.id} onMouseDown={beginMove(i)}>
                                                <rect
                                                    x={p.x}
                                                    y={p.y}
                                                    width={w}
                                                    height={h}
                                                    rx={Math.max(
                                                        0,
                                                        (g.cornerRadius ?? 0) * Math.min(w, h)
                                                    )}
                                                    ry={Math.max(
                                                        0,
                                                        (g.cornerRadius ?? 0) * Math.min(w, h)
                                                    )}
                                                    fill={fill}
                                                    stroke={stroke}
                                                    strokeWidth={isSelected ? 2 : 1}
                                                    strokeDasharray={strokeDash}
                                                />
                                                <text
                                                    x={p.x + 4}
                                                    y={p.y + 14}
                                                    fontSize={11}
                                                    fill={region.visible ? '#1976d2' : '#e65100'}
                                                    style={{ pointerEvents: 'none' }}
                                                >
                                                    {region.label}
                                                    {!region.visible ? ' (hidden)' : ''}
                                                </text>
                                                {isSelected &&
                                                    renderHandlesForRect(
                                                        i,
                                                        g,
                                                        toPx,
                                                        beginResizeRect
                                                    )}
                                            </g>
                                        );
                                    } else {
                                        const c = toPx({ x: g.cx, y: g.cy });
                                        const r = g.r * activeCanvas.width * zoom;
                                        if (r < 0) return null;
                                        return (
                                            <g key={region.id} onMouseDown={beginMove(i)}>
                                                <circle
                                                    cx={c.x}
                                                    cy={c.y}
                                                    r={r}
                                                    fill={fill}
                                                    stroke={stroke}
                                                    strokeWidth={isSelected ? 2 : 1}
                                                    strokeDasharray={strokeDash}
                                                />
                                                <text
                                                    x={c.x}
                                                    y={c.y - r - 4}
                                                    fontSize={11}
                                                    fill={region.visible ? '#1976d2' : '#e65100'}
                                                    textAnchor="middle"
                                                    style={{ pointerEvents: 'none' }}
                                                >
                                                    {region.label}
                                                    {!region.visible ? ' (hidden)' : ''}
                                                </text>
                                                {isSelected &&
                                                    renderHandlesForCircle(
                                                        i,
                                                        g,
                                                        toPx,
                                                        beginResizeCircle
                                                    )}
                                            </g>
                                        );
                                    }
                                })}
                        </svg>
                    </Box>
                ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Upload a background image to begin defining regions.
                    </Typography>
                )}
            </Paper>

            {/* Region list + property editor */}
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <Paper elevation={1} sx={{ width: 300, p: 2 }}>
                    <Typography variant="h6">Regions</Typography>
                    <Stack spacing={1} sx={{ overflowY: 'auto', maxHeight: '40vh', mt: 1 }}>
                        {(regions ?? []).map((region, i) => {
                            const isSelected = i === selectedIndex;
                            const isError = !!errors.regions?.[i];
                            const geometry =
                                currentLanguage === 'en'
                                    ? region.geometry
                                    : (region.geometry_es ?? region.geometry);

                            return (
                                <Box
                                    key={region.id}
                                    sx={{
                                        p: 1,
                                        border: isSelected ? '2px solid #1976d2' : '1px solid #ccc',
                                        borderRadius: 1,
                                        cursor: 'pointer',
                                        boxShadow: isSelected ? 3 : 0,
                                        transition: 'border 0.2s, box-shadow 0.2s',
                                        backgroundColor: !region.visible
                                            ? 'rgba(255,152,0,0.05)'
                                            : undefined,
                                    }}
                                    onClick={() => setSelectedIndex(i)}
                                >
                                    {geometry.type === 'rect' ? (
                                        <>
                                            <Typography variant="subtitle2">
                                                Rect - {region.label}
                                                {!region.visible && (
                                                    <Typography
                                                        component="span"
                                                        variant="caption"
                                                        color="warning.main"
                                                        sx={{ ml: 0.5 }}
                                                    >
                                                        (hidden)
                                                    </Typography>
                                                )}
                                            </Typography>
                                            <Typography variant="caption">
                                                (x, y): {geometry.x}, {geometry.y}
                                            </Typography>
                                            <br />
                                            <Typography variant="caption">
                                                size: {geometry.width} &times; {geometry.height}
                                            </Typography>
                                            <br />
                                            <Typography variant="caption">
                                                weight: {region.weight}
                                            </Typography>
                                        </>
                                    ) : geometry.type === 'circle' ? (
                                        <>
                                            <Typography variant="subtitle2">
                                                Circle - {region.label}
                                                {!region.visible && (
                                                    <Typography
                                                        component="span"
                                                        variant="caption"
                                                        color="warning.main"
                                                        sx={{ ml: 0.5 }}
                                                    >
                                                        (hidden)
                                                    </Typography>
                                                )}
                                            </Typography>
                                            <Typography variant="caption">
                                                (cx, cy): {geometry.cx}, {geometry.cy}
                                            </Typography>
                                            <br />
                                            <Typography variant="caption">
                                                radius: {geometry.r}
                                            </Typography>
                                            <br />
                                            <Typography variant="caption">
                                                weight: {region.weight}
                                            </Typography>
                                        </>
                                    ) : (
                                        <Typography variant="subtitle2">
                                            Unknown shape type
                                        </Typography>
                                    )}
                                    <ErrorMessage
                                        error={isError ? 'Errors need to be corrected' : ''}
                                    />
                                </Box>
                            );
                        })}
                        {(!regions || regions.length === 0) && (
                            <Typography variant="body2" color="text.secondary">
                                No regions. Use the <b>Rect</b> or <b>Circle</b> tools above.
                            </Typography>
                        )}
                    </Stack>
                </Paper>

                <Paper elevation={1} sx={{ width: 700, p: 2, mr: 2 }}>
                    <Typography variant="h6">Region Properties</Typography>
                    {selectedIndex != null && selectedRegion ? (
                        <Stack
                            direction="row"
                            spacing={2}
                            alignItems="flex-start"
                            key={`${selectedIndex}-${currentLanguage}-${JSON.stringify(selectedRegion?.[currentLanguage === 'en' ? 'geometry' : 'geometry_es'])}`}
                        >
                            <Stack spacing={2} flex={1}>
                                <Stack direction="row" spacing={1}>
                                    <SimpleButton color="error" onClick={removeSelected}>
                                        Delete
                                    </SimpleButton>
                                    <SimpleButton onClick={duplicateSelected}>Copy</SimpleButton>
                                </Stack>

                                <ControlledTextField
                                    name={`regions.${selectedIndex}.label`}
                                    control={control}
                                    label="Label"
                                    placeholder="Region label"
                                    error={errors.regions?.[selectedIndex]?.label}
                                />

                                <ControlledSwitch
                                    name={`regions.${selectedIndex}.visible`}
                                    control={control}
                                    label="Visible to student"
                                />

                                <ControlledTextField
                                    name={`regions.${selectedIndex}.weight`}
                                    control={control}
                                    label="Weight"
                                    type="number"
                                    placeholder="Score contribution when selected"
                                    error={errors.regions?.[selectedIndex]?.weight}
                                />
                            </Stack>

                            <Divider orientation="vertical" flexItem />

                            <Stack spacing={1} flex={1}>
                                <Typography variant="subtitle2">Geometry</Typography>
                                {selectedRegion[
                                    currentLanguage === 'en' ? 'geometry' : 'geometry_es'
                                ]?.type === 'rect' && (
                                    <Stack spacing={1}>
                                        {(
                                            ['x', 'y', 'width', 'height', 'cornerRadius'] as const
                                        ).map((key) => (
                                            <ControlledTextField
                                                key={key}
                                                name={`regions.${selectedIndex}.${currentLanguage === 'en' ? 'geometry' : 'geometry_es'}.${key}`}
                                                control={control}
                                                label={key}
                                                type="number"
                                                inputProps={{
                                                    step: 0.002,
                                                    min: 0,
                                                    max: 1,
                                                }}
                                                onChange={(e, field) => {
                                                    field.onChange(snap(e.target.value));
                                                }}
                                                placeholder={`Enter ${key}`}
                                            />
                                        ))}
                                    </Stack>
                                )}
                                {selectedRegion[
                                    currentLanguage === 'en' ? 'geometry' : 'geometry_es'
                                ]?.type === 'circle' && (
                                    <Stack spacing={1}>
                                        {(['cx', 'cy', 'r'] as const).map((key) => (
                                            <ControlledTextField
                                                key={key}
                                                name={`regions.${selectedIndex}.${currentLanguage === 'en' ? 'geometry' : 'geometry_es'}.${key}`}
                                                control={control}
                                                label={key}
                                                type="number"
                                                inputProps={{
                                                    step: 0.002,
                                                    min: 0,
                                                    max: 1,
                                                }}
                                                onChange={(e, field) => {
                                                    field.onChange(snap(e.target.value));
                                                }}
                                                placeholder={`Enter ${key}`}
                                            />
                                        ))}
                                    </Stack>
                                )}
                            </Stack>
                        </Stack>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            Select a region to edit its properties.
                        </Typography>
                    )}
                </Paper>
            </Stack>
        </Stack>
    );
}

// ── Drawing helpers ────────────────────────────────────────────────────
function renderHandlesForRect(
    index: number,
    g: RectDropZoneGeometry,
    toPx: (p: NormalizedPoint) => { x: number; y: number },
    beginResizeRect: (i: number, handle: 'se') => (e: React.MouseEvent) => void
) {
    const p = toPx({ x: g.x, y: g.y });
    const wpx = g.width * (toPx({ x: 1, y: 0 }).x - toPx({ x: 0, y: 0 }).x);
    const hpx = g.height * (toPx({ x: 0, y: 1 }).y - toPx({ x: 0, y: 0 }).y);
    const corners = [{ id: 'se', x: p.x + wpx, y: p.y + hpx }];

    return corners.map((c) => (
        <rect
            key={c.id}
            x={c.x - HANDLE_SIZE / 2}
            y={c.y - HANDLE_SIZE / 2}
            width={HANDLE_SIZE}
            height={HANDLE_SIZE}
            fill="#fff"
            stroke="#1976d2"
            strokeWidth={1}
            onMouseDown={beginResizeRect(index, 'se')}
        />
    ));
}

function renderHandlesForCircle(
    index: number,
    g: CircleDropZoneGeometry,
    toPx: (p: NormalizedPoint) => { x: number; y: number },
    beginResizeCircle: (i: number) => (e: React.MouseEvent) => void
) {
    const c = toPx({ x: g.cx, y: g.cy });
    const rPx = g.r * (toPx({ x: 1, y: 0 }).x - toPx({ x: 0, y: 0 }).x);
    const handle = { x: c.x + rPx, y: c.y };
    return (
        <circle
            cx={handle.x}
            cy={handle.y}
            r={HANDLE_SIZE / 2}
            fill="#fff"
            stroke="#1976d2"
            strokeWidth={1}
            onMouseDown={beginResizeCircle(index)}
        />
    );
}

// ── Utils ──────────────────────────────────────────────────────────────
const SNAP_STEP = 0.002;
function snap(n: number | string, step = SNAP_STEP) {
    const k = Math.round(1 / step);
    return clamp(Math.round(Number(n) * k) / k, 0, 1);
}
function clamp(n: number, min: number, max: number) {
    return Math.max(min, Math.min(max, n));
}
function deepCopy<T>(o: T): T {
    return JSON.parse(JSON.stringify(o));
}
